/**
 * Dynamic API URL Utility
 *
 * Dynamically constructs the API base URL based on the current browser origin.
 * This allows the frontend to automatically adapt to different hosts/domains
 * without hardcoding the API URL.
 *
 * Configuration Priority:
 * 1. Runtime config (window.__RUNTIME_CONFIG__.api.baseURL) - explicit override
 * 2. Environment variable (REACT_APP_API_URL) - explicit override
 * 3. Dynamic URL construction from window.location
 *
 * Usage:
 * import { getApiBaseUrl } from '@utils/dynamicApiUrl';
 * const apiUrl = getApiBaseUrl();
 */

/**
 * Default API port offset from frontend port
 * If frontend runs on port 3000, API will be on 3000 + API_PORT_OFFSET = 5000
 * Set to 0 if API runs on a fixed port defined by REACT_APP_API_PORT
 */
const DEFAULT_API_PORT_OFFSET = 2000;

/**
 * Default fixed API port (used when REACT_APP_API_PORT is set)
 */
const DEFAULT_API_PORT = 5000;

/**
 * API path prefix (e.g., '/api' for http://localhost:5000/api)
 */
const DEFAULT_API_PATH = '';

/**
 * Check if a URL is explicitly configured (not a placeholder)
 * @param {string} url - URL to check
 * @returns {boolean} - True if URL is explicitly configured
 */
const isExplicitUrl = (url) => {
  if (!url) return false;
  // Check if it's a valid absolute URL (not a placeholder or empty)
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get environment variables directly (webpack DefinePlugin requires static access)
 * Dynamic property access like process.env[key] does NOT work with DefinePlugin
 */
const getEnvVar = (key) => {
  switch (key) {
    case 'REACT_APP_API_PORT':
      return process.env.REACT_APP_API_PORT;
    case 'REACT_APP_API_PORT_OFFSET':
      return process.env.REACT_APP_API_PORT_OFFSET;
    case 'REACT_APP_API_PATH':
      return process.env.REACT_APP_API_PATH;
    case 'REACT_APP_USE_DYNAMIC_API_URL':
      return process.env.REACT_APP_USE_DYNAMIC_API_URL;
    case 'REACT_APP_API_URL':
      return process.env.REACT_APP_API_URL;
    default:
      return undefined;
  }
};

/**
 * Get configuration value from runtime config or environment
 * @param {string} runtimePath - Path in runtime config (dot notation)
 * @param {string} envKey - Environment variable key
 * @param {*} defaultValue - Default value if not found
 * @returns {*} - Configuration value
 */
const getConfigValue = (runtimePath, envKey, defaultValue) => {
  // Check runtime config first
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
    const parts = runtimePath.split('.');
    let value = window.__RUNTIME_CONFIG__;
    for (const part of parts) {
      value = value?.[part];
      if (value === undefined) break;
    }
    if (value !== undefined) return value;
  }

  // Check environment variable using static access
  const envValue = getEnvVar(envKey);
  if (envValue !== undefined) {
    return envValue;
  }

  return defaultValue;
};

/**
 * Detect if we're in a server-side rendering context
 * @returns {boolean}
 */
const isSSR = () => {
  return typeof window === 'undefined';
};

/**
 * Get the current browser origin information
 * @returns {Object} - { protocol, hostname, port }
 */
export const getOriginInfo = () => {
  if (isSSR()) {
    return {
      protocol: 'http:',
      hostname: 'localhost',
      port: '3000',
    };
  }

  const { protocol, hostname, port } = window.location;

  return {
    protocol,
    hostname,
    port: port || (protocol === 'https:' ? '443' : '80'),
  };
};

/**
 * Calculate the API port based on configuration
 * @param {string} frontendPort - Current frontend port
 * @returns {string} - API port
 */
export const calculateApiPort = (frontendPort) => {
  // Check for explicit API port configuration
  const explicitPort = getConfigValue('api.port', 'REACT_APP_API_PORT', null);
  if (explicitPort) {
    return String(explicitPort);
  }

  // Check for port offset configuration
  const portOffset = getConfigValue(
    'api.portOffset',
    'REACT_APP_API_PORT_OFFSET',
    DEFAULT_API_PORT_OFFSET
  );

  // Calculate port with offset
  const frontendPortNum = parseInt(frontendPort, 10);
  if (!isNaN(frontendPortNum) && portOffset !== null) {
    return String(frontendPortNum + parseInt(portOffset, 10));
  }

  // Fallback to default API port
  return String(DEFAULT_API_PORT);
};

/**
 * Build the dynamic API base URL
 * @param {Object} options - Configuration options
 * @param {string} options.protocol - Protocol to use (default: auto-detect)
 * @param {string} options.hostname - Hostname to use (default: auto-detect)
 * @param {string} options.port - Port to use (default: calculated)
 * @param {string} options.path - API path prefix (default: from config or '')
 * @returns {string} - Complete API base URL
 */
export const buildApiUrl = (options = {}) => {
  const origin = getOriginInfo();

  const protocol = options.protocol || origin.protocol;
  const hostname = options.hostname || origin.hostname;
  const port = options.port || calculateApiPort(origin.port);
  const path = options.path ?? getConfigValue('api.path', 'REACT_APP_API_PATH', DEFAULT_API_PATH);

  // Build the URL
  // For standard ports (80 for http, 443 for https), omit the port
  let url = `${protocol}//${hostname}`;

  const isStandardPort =
    (protocol === 'https:' && port === '443') || (protocol === 'http:' && port === '80');

  if (!isStandardPort && port) {
    url += `:${port}`;
  }

  // Append path if provided
  if (path) {
    url += path.startsWith('/') ? path : `/${path}`;
  }

  return url;
};

/**
 * Get the API base URL with fallback logic
 *
 * Priority:
 * 1. If useDynamicUrl is true, always build dynamic URL
 * 2. Explicit runtime config URL (window.__RUNTIME_CONFIG__.api.baseURL)
 * 3. Explicit environment variable URL (REACT_APP_API_URL)
 * 4. Dynamic URL construction from current origin
 *
 * @returns {string} - API base URL
 */
export const getApiBaseUrl = () => {
  // Check if dynamic mode is enabled FIRST
  // Handle both boolean (from runtime config) and string (from env var) values
  const dynamicUrlConfig = getConfigValue('api.useDynamicUrl', 'REACT_APP_USE_DYNAMIC_API_URL', false);
  const useDynamicUrl = dynamicUrlConfig === true || dynamicUrlConfig === 'true';

  // If dynamic URL mode is enabled, always build from current origin
  if (useDynamicUrl) {
    const dynamicUrl = buildApiUrl();
    console.log('[API] Using dynamic URL (useDynamicUrl=true):', dynamicUrl);
    return dynamicUrl;
  }

  // Check for explicit runtime config
  const runtimeUrl =
    typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__?.api?.baseURL : null;

  if (isExplicitUrl(runtimeUrl)) {
    console.log('[API] Using runtime config URL:', runtimeUrl);
    return runtimeUrl;
  }

  // Check for explicit environment variable
  const envUrl = process.env.REACT_APP_API_URL;

  if (isExplicitUrl(envUrl)) {
    console.log('[API] Using environment variable URL:', envUrl);
    return envUrl;
  }

  // Build dynamic URL as fallback
  const dynamicUrl = buildApiUrl();
  console.log('[API] Using dynamic URL:', dynamicUrl);
  return dynamicUrl;
};

/**
 * Get complete API endpoint URL
 * @param {string} endpoint - Relative endpoint path
 * @returns {string} - Complete URL
 */
export const getApiEndpoint = (endpoint) => {
  const baseUrl = getApiBaseUrl();
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`;
};

export default {
  getApiBaseUrl,
  getApiEndpoint,
  getOriginInfo,
  calculateApiPort,
  buildApiUrl,
};
